import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import emailIcon from '../../../assets/images/login-icon-email.svg';
import lockIcon from '../../../assets/images/login-icon-password.svg';
import {
  focusedInputBorderColor,
  unfocusedInputBorderColor,
  appTextColor,
  appMainBackground,
  white,
  warningRedColor,
} from '../../../styles/colors';
import { withWindowDimensions } from '@rosetta/react-common-ui';

import PropTypes from 'prop-types';
import { appSelectors } from '../reducer/app-reducer';
import { isMobile as isMobileDetected } from 'react-device-detect';

let isRtl = false;
const startSidePadding = 43;
const endSidePadding = 12;

const InputWithIconDiv = styled.div((props) => {
  let style = {
    display: 'block',
    position: 'relative',
    flexShrink: '0',
    height: props.outsideHeight + 'px',
    width: '100%'
  };
  return style;
});

const InputWithIconInput = styled.input((props) => {
  const bgColor = props.isMobile ? white : appMainBackground;
  let style = {
    lineHeight: '18px',
    height: '100%',
    width: '100%',
    border: '1px solid ' + unfocusedInputBorderColor,
    borderRadius: '4px',
    color: appTextColor,
    backgroundColor: bgColor,
    fontSize: '14px',
    fontFamily: 'inherit',
    paddingTop: '0px',
    fontWeight: '200',
    '::placeholder': {
      fontWeight: '200'
    },
    ':focus': {
      outline: 'none',
      borderColor: focusedInputBorderColor
    },
    // this is a hack/clever fix which, when Chrome puts a yellow background to show that it
    //  has autofilled the input, adds a dropshadow which *looks* just like our background color
    //  because Chrome will not let us control the background color no matter what
    ':-webkit-autofill': {
      boxShadow: '0 0 0 ' + props.outsideHeight + 'px ' + bgColor + ' inset'
    }
  };
  if (props.displayError) {
    style.borderColor = warningRedColor;
    style[':focus'].borderColor = warningRedColor;
  }
  // make room for the icon on the "start" side of the input
  if (isRtl) {
    style.paddingLeft = endSidePadding + 'px';
    style.paddingRight = startSidePadding + 'px';
  } else {
    style.paddingLeft = startSidePadding + 'px';
    style.paddingRight = endSidePadding + 'px';
  }
  return style;
});

const InputWithIconIcon = styled.div((props) => {
  let style = {
    display: 'block',
    position: 'absolute',
    top: props.iconTopPad,
    width: props.iconWidth + 'px',
    height: props.iconHeight + 'px',
    backgroundColor: !props.isMobile ? appMainBackground : white,
    backgroundRepeat: 'no-repeat',
    backgroundSize: props.iconWidth + 'px ' + props.iconHeight + 'px',
    backgroundImage: props.iconUrl
  };
  if (isRtl) {
    style.right = props.iconSidePad + 'px';
  } else {
    style.left = props.iconSidePad + 'px';
  }
  return style;
});

//*********************************************
class InputWithIcon extends PureComponent {
  componentWillMount = () => {};

  noop = () => {};

  render = () => {
    const isMobile = isMobileDetected;
    isRtl = this.props.isInterfaceRtl;

    let iconUrl = '';
    let iconWidth = 0;
    let iconHeight = 0;
    // icon position is set for standard outsideHeight of 40 and adjusted
    //  because the known icon position has been lovingly hand-crafted
    const topPadAdjustment = Math.round((40 - this.props.outsideHeight) / 2);
    let iconTopPad = 0;
    let iconSidePad = 0;
    if (this.props.specialIconUrl && this.props.specialIconSize) {
      iconUrl = this.props.specialIconUrl;
      iconWidth = this.props.specialIconSize.width;
      iconHeight = this.props.specialIconSize.height;
      // approximately centered vertically
      iconTopPad = Math.round((this.props.outsideHeight - iconHeight) / 2) - 1;
      // positioned about right horizontally
      iconSidePad = Math.round((38 - iconWidth) / 2);
    } else {
      if (this.props.inputType === 'password') {
        iconUrl = 'url(' + lockIcon + ')';
        iconWidth = 15;
        iconHeight = 20;
        iconTopPad = 9 - topPadAdjustment;
        iconSidePad = 13;
      } else {
        iconUrl = 'url(' + emailIcon + ')';
        iconWidth = 20;
        iconHeight = 14;
        iconTopPad = 13 - topPadAdjustment;
        iconSidePad = 10;
      }
    }

    let iconWidget = null;
    if (iconHeight > 0 && iconWidth > 0) {
      iconWidget = (
        <InputWithIconIcon
          data-qa="InputWithIconIcon"
          iconUrl={iconUrl}
          iconWidth={iconWidth}
          iconHeight={iconHeight}
          iconTopPad={iconTopPad}
          iconSidePad={iconSidePad}
          isMobile={isMobile}
        />
      );
    }

    const tabNdx = this.props.tabIndex ? this.props.tabIndex : 0;
    const takeFocus = this.props.autoFocus ? this.props.autoFocus : false;

    return (
      <InputWithIconDiv data-qa="InputWithIconDiv" outsideHeight={this.props.outsideHeight}>
        <InputWithIconInput
          type={this.props.inputType}
          data-qa={this.props.dataQa || 'InputWithIconInput'}
          placeholder={this.props.placeholder}
          value={this.props.value}
          autoFocus={takeFocus}
          onKeyPress={this.props.onKeyPress || this.noop}
          onChange={this.props.onChange || this.noop}
          onBlur={this.props.onBlur || this.noop}
          outsideHeight={this.props.outsideHeight}
          displayError={this.props.displayError}
          tabIndex={tabNdx}
          isMobile={isMobile}
        />
        {iconWidget}
      </InputWithIconDiv>
    );
  };
}

InputWithIcon.propTypes = {
  // all the familiar names get passed right through to the input element
  onKeyPress: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  autoFocus: PropTypes.bool,

  // dataQa gets put this on the input element data-qa
  dataQa: PropTypes.string,

  // if you want the icon in the input to be something besides
  //  either the lock or the envelope, hand it in as e.g. 'url(' + theImportedFile + ')'
  //  if specialIconSize e.g. { width: 20, height: 22 } has 0 for its values,
  //  then no specialIcon will show.  Else it will displace envelope and lock.
  specialIconUrl: PropTypes.string,
  specialIconSize: PropTypes.object,

  // inputType should be either "password" or "text"
  inputType: PropTypes.string.isRequired,

  placeholder: PropTypes.string.isRequired,
  tabIndex: PropTypes.number,
  value: PropTypes.string.isRequired,

  // how tall is it - it always will get width as 100% of its container
  outsideHeight: PropTypes.number.isRequired,

  // if you want this field to be outlined in red, make this true
  displayError: PropTypes.bool
};

const mapStateToProps = (state, ownprops) => ({
  isInterfaceRtl: appSelectors.isInterfaceRtl(state)
});

const wrapActions = {};
export const InputWithIconContainer = connect(
  mapStateToProps,
  wrapActions
)(withWindowDimensions(InputWithIcon));



// WEBPACK FOOTER //
// ./src/modules/app/util/InputWithIcon.jsx